<template>
  <div
    class="c-alert"
    :class="{
      [`c-alert--${variant}`]: variant,
      'is-dismissable': isDismissable,
      'no-border': noBorder,
      hidden: isHidden,
    }"
    role="alert"
  >
    <div class="text-gray-800 c-alert__content">
      <div class="c-alert__container">
        <CIcon
          :name="iconName"
          class="text-gray-800"
        />
        <div class="c-alert__container--text">
          <h3
            v-if="title && title.length > 0"
            class="c-alert--title"
          >
            {{ title }}
          </h3>
          <slot name="text" />
        </div>
      </div>
      <button
        v-if="isDismissable"
        type="button"
        class="c-alert--dismiss-btn"
        title="Close"
        aria-label="Close"
        @click="dismiss"
      >
        <CIcon
          name="close"
          class="text-gray-800"
        />
      </button>
    </div>
    <div class="c-alert__link-container">
      <component
        :is="linkTag(link) as string"
        v-for="(link, index) in validLinks"
        :key="index"
        :to="link?.to"
        :href="link?.href"
        class="c-alert--link"
        name="link"
      >
        {{ link?.text }}
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, resolveComponent } from 'vue'
import CIcon from '../../atoms/CIcon'

interface Link {
  text: string
  to?: string
  href?: string
}

interface Props {
  variant: 'error' | 'warning' | 'success' | 'info' | 'tip'
  title?: string
  isDismissable?: boolean
  links?: Link[]
  noBorder?: boolean
  hideOnDismiss?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  isDismissable: false,
  links: () => [],
  noBorder: false,
  hideOnDismiss: false,
})

const emit = defineEmits<{
  (e: 'dismissed'): void
}>()

const isHidden = ref(false)

const iconName = computed(() => {
  switch (props.variant) {
    case 'error':
    case 'warning':
      return 'warning'
    case 'success':
      return 'check_circle'
    case 'tip':
      return 'lightbulb'
    default:
      return 'info'
  }
})

const validLinks = computed(() => {
  return props.links.slice(0, 2).filter((link) => {
    if (link && link?.text && (link?.href || link?.to)) return true

    return false
  })
})

const linkTag = (link: Link) => {
  if (link?.href) return 'a'
  if (link?.to) return resolveComponent('nuxt-link')

  return null
}

const dismiss = () => {
  if (props.hideOnDismiss) {
    isHidden.value = true
  }
  emit('dismissed')
}
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CAlert',
})
</script>
